<template>
    <wrapper-features-templates-operations
    :hideContentWrapper="!showLoader && !applyErrorState && showEmptyState"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :tokenTitleAppend="t('cards.block.tokenModalTitle')"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="errorHandler($event, unblockCardHandler)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- *********************************************
        *												 *
        *                     Content                    *
        *												 *
        ********************************************** -->
        <div
        v-if="!showLoader && !applyErrorState && !showEmptyState"
        class="content-container"
        :class="{ mobile: getMobileAppState }">
            <!-- Note Text Block -->
            <text-block-generic :params="{ content: t('cards.block.startNote') }"/>
            <!-- Card Dropdown -->
            <dropdown-card
            :params="{ labelText: t('cards.block.dropdownCardLabel'), withBorder: true }"
            :options="getCardsList"
            :selectedOption="selectedCard"
            @clicked="selectedCard = $event"/>
            <!-- Card Details -->
            <divider-collapsible
            doubleColumn
            startClosed
            :class="{'mobile': getMobileAppState}"
            :title="t('textBlocks.showDetails')"
            :openTitle="t('textBlocks.hideDetails')">
                <!-- Text Block -->
                <text-block-generic
                v-for="(entry, index) in commonCardDetailParser(selectedCardInfo)"
                :key="index.key"
                :params="entry"
                :applyEllipsis="entry.applyEllipsis"
                :mask="entry.mask"/>
            </divider-collapsible>
            <!-- Divider -->
            <divider-generic />
            <!-- Card Status -->
            <text-block-generic
            :params="{
                label: t('cards.block.cardStatusLabel'),
                content: selectedCardInfo.unblockable ? t('cards.blockedStatus') : t('cards.unblockedStatus')
            }"/>
            <!-- Info Box -->
            <infobox-generic
            :label="selectedCardInfo.blockable ? t('cards.block.unblockedCardInfoText') : t('cards.block.blockedCardInfoText')"/>
            <!-- Button -->
            <button-tertiary
            :label="selectedCardInfo.blockable ? t('cards.block.blockCard') : t('cards.block.unblockCard')"
            @clicked="buttonHandler"/>
        </div>
        <!-- Empty-state -->
        <template #empty-state-slot>
            <transition name="common-enter-animation" mode="out-in" appear>
                <empty-state-generic
                v-if="showEmptyState && !showLoader && !applyErrorState"
                illustration="emptyState/cards-generic"
                :title="t('cards.block.emptyStateTitle')"
                :description="t('cards.block.emptyStateDescription')"/>
            </transition>
        </template>
    </wrapper-features-templates-operations>
    <!-- Block Modal -->
    <modal-centered-generic
    v-if="showBlockModal"
    negative
    :title="blockModalInfo.title"
    :text="blockModalInfo.text"
    :leftButtonText="blockModalInfo.leftButtonText"
    :rightButtonText="blockModalInfo.rightButtonText"
    @hideMe="showBlockModal = false"
    @rightButtonClicked="blockCardHandler"/>
    <!-- Feedback Toasts -->
    <feedback-toast
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import DividerCollapsible from '@/components/generic/containers/Collapsible'
import DropdownCard from '@/components/generic/dropdowns/cards/Card'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import FeedbackToast from '@/components/generic/toasts/Generic'
/* Vue */
import { ref, computed, onBeforeUnmount } from 'vue'
/* Store */
import { mapGetters, mapActions } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
/* Aux */
import { commonCardDetailParser } from '@/assets/scripts/parsers/cards'
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestCardsList, blockCard, unblockCard, clearFeatureStates } = mapActions(moduleMapper.CARDS_BLOCK)
const { getCardsList } = mapGetters(moduleMapper.CARDS_BLOCK)
const { getMobileAppState } = mapGetters()

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const showBlockModal = ref(false)

/*************************************************
*                                                *
*                       Data                     *
*                                                *
*************************************************/
const blockModalInfo = {
    title: t('cards.block.blockModal.title'),
    text: t('cards.block.blockModal.text'),
    leftButtonText: t('cards.block.blockModal.leftButtonText'),
    rightButtonText: t('cards.block.blockModal.rightButtonText')
}

const selectedCardInfo = computed(() => {
    return getCardsList.value.find(card => card.id === selectedCard.value)
})

const buttonHandler = () => {
    selectedCardInfo.value.blockable ? showBlockModal.value = true : unblockCardHandler()
}

const showEmptyState = computed(() => {
    return !selectedCard.value
})

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const selectedCard = ref('')
const cardsList = async () => {
	showLoader.value = true
    applyErrorState.value = false
	showFeedbackToast.value = false

    try {
        await requestCardsList()
        /* Setting Initial Selected Card */
        if (getCardsList.value.length) selectedCard.value = getCardsList.value[0].id
        showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
        applyErrorState.value = true
		errorHandler(error.response.data, cardsList)
	}
}

const blockCardHandler = async () => {
    showBlockModal.value = false
    showOverlayLoader.value = true
	showFeedbackToast.value = false

    try {
        await blockCard({ id: selectedCardInfo.value.id })
        await requestCardsList()

        showOverlayLoader.value = false

        feedbackToastData.value = sucessObjectConstrutor(t('cards.block.cardBlockedSuccess'))
        showFeedbackToast.value = true
    } catch (error) {
        if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, blockCardHandler)
    }
}

const unblockCardHandler = async () => {
    showOverlayLoader.value = true
	showFeedbackToast.value = false

    try {
        await unblockCard({ id: selectedCardInfo.value.id })

        showOverlayLoader.value = false
    } catch (error) {
        if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, blockCardHandler)
    }
}

const challengeSuccessHandler = () => {
    cardsList()

    feedbackToastData.value = sucessObjectConstrutor(t('cards.block.cardUnblockedSuccess'))
    showFeedbackToast.value = true
}
/*************************************************
*                                                *
*                 Error Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showOverlayLoader.value = false
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

cardsList()

onBeforeUnmount(() => {
    clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.content-container {
    display: flex;
    flex-direction: column;
    gap: 24px;

    width: 100%;

    &:not(.mobile) {
        margin-top: 32px;
    }

    .collapsible-divider-wrapper {
        padding: 0;
    }

    .divider-generic-wrapper {
        padding-top: 0;
    }
}
</style>
